import f from '@app/icare/db/schema/field/f'
import { PatientRelation } from '@app/icare/entities/patient/schema/PatientRelation'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import dayjs from 'dayjs'
import { z } from 'zod'

export const PatientCostCarrierExemptionId = entityId('patientCostCarrierExemption')
export type PatientCostCarrierExemptionIdType = typeof PatientCostCarrierExemptionId.Type

export const PatientCostCarrierExemptionVerificationStatusEnum = defineEnum({
  OPEN: { label: msg`Offen`, color: 'default' as const },
  VERIFIED: { label: msg`Verifiziert`, color: 'green' as const },
  REJECTED: { label: msg`abgelehnt`, color: 'red' as const },
})
export type PatientCostCarrierExemptionVerificationStatusType =
  typeof PatientCostCarrierExemptionVerificationStatusEnum.$.Type

export const PatientCostCarrierExemptionSchema = PatientRelation.extend({
  id: PatientCostCarrierExemptionId.schema,
  patientCostCarrierId: z.templateLiteral(['patientCostCarrier_', z.string()]),
  fileName: z.string().nullish(),
  uploadedAt: f.date().nullish(),
  verificationStatus: z.enum(PatientCostCarrierExemptionVerificationStatusEnum.$.zodEnum()),
})
export type PatientCostCarrierExemption = z.infer<typeof PatientCostCarrierExemptionSchema>

export const PatientCostCarrierExemptionDocumentStatusEnum = defineEnum({
  OPEN: { label: msg`Offen`, color: 'default' as const },
  RUNNING: { label: msg`laufend`, color: 'green' as const },
  EXPIRED: { label: msg`abgelaufen`, color: 'red' as const },
  PLANNED: { label: msg`geplant`, color: 'blue' as const },
}).$.withParsers({
  fromExemption: (exemption: PatientCostCarrierExemption, today: dayjs.Dayjs = dayjs()) => {
    const from = exemption.activeFrom ? dayjs(exemption.activeFrom) : undefined
    const to = exemption.activeTo ? dayjs(exemption.activeTo) : undefined
    if (!from && !to) return 'OPEN'
    if (to && today.isAfter(to, 'day')) return 'EXPIRED'
    if (from && today.isBefore(from, 'day')) return 'PLANNED'
    return 'RUNNING'
  },
})
export type PatientCostCarrierExemptionDocumentStatusType = typeof PatientCostCarrierExemptionDocumentStatusEnum.$.Type

/**
 * Maps a legacy `documentStore_<uuid>` id to the new table-routed id. Deterministic so the
 * server and every offline client derive identical ids from the same source JSON during the
 * data migration — no duplicates after the next sync.
 */
export const deriveExemptionId = (oldId: string): PatientCostCarrierExemptionIdType => {
  const suffix = oldId.includes('_') ? oldId.slice(oldId.indexOf('_') + 1) : oldId
  return `patientCostCarrierExemption_${suffix}` as PatientCostCarrierExemptionIdType
}
